import * as React from "react";

/**
 * Navbar visibility context for sharing navbar hidden/visible state
 * across components without requiring direct DOM queries.
 */

// Desktop heights in pixels - used as defaults before measurement
export const NAVBAR_HEIGHTS = {
  BANNER: 48,
  NAVBAR: 68,
  GAP: 24,
} as const;

export type NavbarVisibilityContextProps = {
  isHidden: boolean;
  setIsHidden: (hidden: boolean) => void;
  /** Actual measured height of the navbar container (banner + nav + border). Responsive to viewport. */
  measuredHeight: number;
};

const defaultNavbarVisibilityContext: NavbarVisibilityContextProps = {
  isHidden: false,
  setIsHidden: () => {},
  measuredHeight: NAVBAR_HEIGHTS.BANNER + NAVBAR_HEIGHTS.NAVBAR,
};

export const NavbarVisibilityContext =
  React.createContext<NavbarVisibilityContextProps>(
    defaultNavbarVisibilityContext,
  );

/** Internal-only context for Navbar to write the measured height. Not exported. */
const NavbarHeightSetterContext = React.createContext<(height: number) => void>(
  () => {},
);

export const useNavbarHeightSetter = (): ((height: number) => void) => {
  return React.useContext(NavbarHeightSetterContext);
};

export const NavbarVisibilityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isHidden, setIsHidden] = React.useState(false);
  const [measuredHeight, setMeasuredHeight] = React.useState(
    NAVBAR_HEIGHTS.BANNER + NAVBAR_HEIGHTS.NAVBAR,
  );

  const value = React.useMemo(
    () => ({ isHidden, setIsHidden, measuredHeight }),
    [isHidden, measuredHeight],
  );

  return (
    <NavbarVisibilityContext.Provider value={value}>
      <NavbarHeightSetterContext.Provider value={setMeasuredHeight}>
        {children}
      </NavbarHeightSetterContext.Provider>
    </NavbarVisibilityContext.Provider>
  );
};

export const useNavbarVisibility = (): NavbarVisibilityContextProps => {
  return React.useContext(NavbarVisibilityContext);
};
